import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { UserCompany } from "../services/api";

interface LocationState {
  token: string;
  userNo: number;
  userName: string;
  companies: UserCompany[];
}

const BaevrCompanySelection: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state as LocationState | null;

  const token = state?.token || "";
  const userNo = state?.userNo || 0;
  const userName = state?.userName || "";
  const companies = state?.companies || [];

  // =========================================================
  // SELECT COMPANY
  // =========================================================
  const handleSelectCompany = (company: UserCompany) => {
    if (!window.chrome?.webview) {
      console.error("WebView2 is not available.");
      return;
    }

    const loginMessage = JSON.stringify({
      type: "BAEVR_LOGIN_SUCCESS",
      token: token,
      userNo: userNo,
      userName: userName,
      companyNo: company.companyNo,
      userType: company.userType || "",
    });

    window.chrome.webview.postMessage(loginMessage);
  };

  // =========================================================
  // BACK
  // =========================================================
  const handleBack = () => {
    navigate("/baevr-desktop");
  };

  // =========================================================
  // NO LOGIN DATA
  // =========================================================
  if (!state || !token || !userNo || companies.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Company Selection
          </h1>

          <p className="text-sm text-red-500 mt-3">
            No company information is available.
          </p>

          <button
            onClick={handleBack}
            className="mt-6 px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-6">

      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">

        {/* =====================================================
            HEADER
        ===================================================== */}
        <div className="flex justify-between items-center px-6 py-4 border-b">

          <div>
            <h1 className="text-xl font-semibold text-gray-800">
              Select Company
            </h1>

            <p className="text-sm text-gray-500 mt-1">
              Select a company to continue
            </p>

            {userName && (
              <p className="text-xs text-gray-400 mt-1">
                User: {userName}
              </p>
            )}
          </div>

          <button
            onClick={handleBack}
            className="text-gray-500 hover:text-gray-800 text-xl"
          >
            ✕
          </button>

        </div>

        {/* =====================================================
            CONTENT
        ===================================================== */}
        <div className="p-6">

          <div className="space-y-3">

            {companies.map((company) => (

              <button
                key={company.companyNo}
                onClick={() => handleSelectCompany(company)}
                className="w-full text-left border rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 transition flex items-center justify-between"
              >

                <div>

                  <div className="font-semibold text-gray-800">
                    {company.companyName}
                  </div>

                  <div className="text-sm text-gray-500 mt-1">
                    Company No: {company.companyNo}
                  </div>

                </div>

                <div className="flex items-center gap-3">

                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                    {company.userType}
                  </span>

                  <span className="text-gray-400 text-lg">
                    →
                  </span>

                </div>

              </button>

            ))}

          </div>

        </div>

        {/* =====================================================
            FOOTER
        ===================================================== */}
        <div className="border-t px-6 py-4">

          <button
            onClick={handleBack}
            className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-100"
          >
            Back to Login
          </button>

        </div>

      </div>

    </div>
  );
};

export default BaevrCompanySelection;